import {useCallback} from 'react'
import {usePullRequestAnalytics} from './use-pull-request-analytics'
import {useActiveChangeGroup} from '../stores/selectors'
import type {ChangeGroupGenerationUnavailableReasons} from '../types/group-types'

/**
 * Hook for sending analytics events related to change groups presentation.
 * This helps track visibility and usage of the change groups feature.
 */
export function useChangeGroupsAnalytics() {
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const activeChangeGroup = useActiveChangeGroup()

  /**
   * Sends analytics event when change groups are presented to the user.
   * This includes cases where groups are shown, hidden by preference, or in error states.
   */
  const sendChangeGroupsPresentedEvent = useCallback(
    ({
      isErrorState = false,
      unavailableReason,
      userPreferenceDisabled = false,
      hasGroups = false,
    }: {
      isErrorState?: boolean
      unavailableReason?: ChangeGroupGenerationUnavailableReasons
      userPreferenceDisabled?: boolean
      hasGroups?: boolean
    }) => {
      const payload: {[key: string]: unknown} = {
        presented_and_visible: hasGroups && !isErrorState && !userPreferenceDisabled,
        presented_and_not_visible: hasGroups && !isErrorState && userPreferenceDisabled,
        has_groups: hasGroups,
        is_error_state: isErrorState,
        selected_group_id: activeChangeGroup?.id,
        browser_width: typeof window !== 'undefined' ? window.innerWidth : 0,
        ...(unavailableReason && {unavailable_reason: unavailableReason}),
        ...(activeChangeGroup && {selected_group_name: activeChangeGroup.name}),
      }

      sendPullRequestAnalyticsEvent('change_groups.presented', 'CHANGE_GROUPS_PRESENTED', payload)
    },
    [sendPullRequestAnalyticsEvent, activeChangeGroup],
  )

  return {
    sendChangeGroupsPresentedEvent,
  }
}
